import { Controller } from "@hotwired/stimulus"
import { describeRange, resolveAnchor, wrapSpan, sectionHeading, sectionLabel, scrollBehavior } from "./text_anchor"

/**
 * Student highlights & notes on the modern book reader (docs/highlights.md).
 *
 * Lives on <body> (layouts/modern_reader) so it reaches the reading column,
 * the right-hand tools rail, the panel and the phone's topbar button.
 *
 * - Paints the student's stored highlights after the theme controllers have
 *   rebuilt the page (two animation frames, as text-anchor does).
 * - A highlight whose anchor no longer resolves is never painted near a
 *   guess. It is listed in the panel as "passage changed" with its saved
 *   quote, and the server is told so the miss is countable per book.
 * - A selection inside the prose opens a colour toolbar: a popover above the
 *   selection on desktop, a bar along the bottom on phones (clear of the
 *   phone's own copy/share callout, which sits by the selection).
 * - The panel shares the right rail with the Book Tutor, one open at a time,
 *   coordinated with `reader-tools:*` window events so neither controller
 *   needs a handle on the other.
 */

const COLORS = ["yellow", "green", "blue", "pink"]
const NAMES = { yellow: "Yellow", green: "Green", blue: "Blue", pink: "Pink" }
const PHONE = "(max-width: 767.98px)"

export default class extends Controller {
  static targets = ["panel", "list", "filters", "toolbar", "count", "railButton", "tutorButton", "status"]
  static values = {
    items: Array,
    assetId: Number,
    url: String,
    scope: { type: String, default: ".reader-annotatable" }
  }

  connect() {
    this.items = this.itemsValue.map((item) => ({ ...item }))
    this.filter = "all"
    this.editing = null
    this.confirming = null // id awaiting "Delete?" in the panel
    this.focusId = null
    this.pending = null // { anchor, range } of a live selection
    this.markFor = null // id of the highlight the toolbar is editing

    this.onSelectionChange = () => {
      clearTimeout(this.selectionTimer)
      this.selectionTimer = setTimeout(() => this.checkSelection(), this.isPhone() ? 250 : 80)
    }
    this.onPointerDown = (event) => {
      if (!this.markFor) return
      if (event.target.closest(".annotation-toolbar, mark.annotation-mark")) return
      this.hideToolbar()
    }
    this.onKeydown = (event) => {
      if (event.key !== "Escape") return
      if (!this.toolbarTarget.hidden) this.hideToolbar()
      else if (this.isOpen()) this.close()
    }
    this.onToolOpened = (event) => {
      const tool = event.detail?.tool
      if (tool !== "annotations" && this.isOpen()) this.close({ silent: true })
      if (tool === "tutor") this.setTutorPressed(true)
    }
    this.onToolClosed = (event) => {
      if (event.detail?.tool === "tutor") this.setTutorPressed(false)
    }

    document.addEventListener("selectionchange", this.onSelectionChange)
    document.addEventListener("pointerdown", this.onPointerDown)
    document.addEventListener("keydown", this.onKeydown)
    window.addEventListener("reader-tools:opened", this.onToolOpened)
    window.addEventListener("reader-tools:closed", this.onToolClosed)

    this.renderList()
    // After key-terms / chapter-quiz / chapter-outline rebuild the page.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.paintAll()
      this.renderList()
      this.jumpFromUrl()
    }))
  }

  disconnect() {
    clearTimeout(this.selectionTimer)
    document.removeEventListener("selectionchange", this.onSelectionChange)
    document.removeEventListener("pointerdown", this.onPointerDown)
    document.removeEventListener("keydown", this.onKeydown)
    window.removeEventListener("reader-tools:opened", this.onToolOpened)
    window.removeEventListener("reader-tools:closed", this.onToolClosed)
    document.documentElement.classList.remove("annotations-open", "annotations-sheet-open", "annotation-selecting")
  }

  // ---- Painting -------------------------------------------------------------

  get prose() {
    return this.element.querySelector(this.scopeValue)
  }

  paintAll() {
    const scope = this.prose
    if (!scope) return

    this.items.forEach((item) => {
      if (!item.anchor) return
      const found = resolveAnchor(scope, item.anchor)
      const orphaned = !found || !this.paint(item, found)
      if (orphaned !== !!item.orphaned) {
        item.orphaned = orphaned
        this.save(item, { orphaned })
      }
    })
  }

  paint(item, found) {
    const marks = wrapSpan(found, () => {
      const mark = document.createElement("mark")
      mark.className = "annotation-mark"
      mark.dataset.annotationId = item.id
      mark.dataset.color = item.color || "yellow"
      return mark
    })
    if (marks) this.syncPin(item)
    return marks
  }

  unpaint(id) {
    const scope = this.prose
    if (!scope) return
    scope.querySelectorAll(`.annotation-pin[data-annotation-id="${id}"]`).forEach((pin) => pin.remove())
    this.marksFor(id).forEach((mark) => {
      const parent = mark.parentNode
      if (!parent) return
      while (mark.firstChild) parent.insertBefore(mark.firstChild, mark)
      mark.remove()
      parent.normalize()
    })
  }

  marksFor(id) {
    return this.prose ? [...this.prose.querySelectorAll(`mark.annotation-mark[data-annotation-id="${id}"]`)] : []
  }

  // A small note button after the highlight's last mark. Icon only, so it adds
  // no text to the index (and text_anchor's SKIP list excludes it anyway).
  syncPin(item) {
    this.prose?.querySelectorAll(`.annotation-pin[data-annotation-id="${item.id}"]`).forEach((pin) => pin.remove())
    const marks = this.marksFor(item.id)
    if (!item.body || !marks.length) return

    const pin = document.createElement("button")
    pin.type = "button"
    pin.className = "annotation-pin"
    pin.dataset.annotationId = item.id
    pin.title = item.body
    pin.setAttribute("aria-label", `Your note: ${truncate(item.body, 120)}`)
    pin.innerHTML = '<i class="bi bi-pencil-fill" aria-hidden="true"></i>'
    marks[marks.length - 1].after(pin)
  }

  // ---- Selection & toolbar ---------------------------------------------------

  checkSelection() {
    if (this.markFor) return
    const scope = this.prose
    const selection = window.getSelection()
    if (!scope || !selection || !selection.rangeCount || selection.isCollapsed) return this.clearPending()

    const range = selection.getRangeAt(0)
    if (!scope.contains(range.commonAncestorContainer)) return this.clearPending()

    const anchor = describeRange(scope, range)
    if (!anchor) return this.clearPending()

    this.pending = { anchor, range: range.cloneRange() }
    this.showToolbar(range.getBoundingClientRect(), null)
  }

  clearPending() {
    this.pending = null
    if (!this.markFor) this.hideToolbar()
  }

  // Clicking a painted highlight opens the same toolbar to recolour, note or delete.
  markClick(event) {
    const pin = event.target.closest(".annotation-pin")
    if (pin) {
      event.preventDefault()
      this.open({ focusId: Number(pin.dataset.annotationId) })
      return
    }

    const mark = event.target.closest("mark.annotation-mark")
    if (!mark || !window.getSelection()?.isCollapsed) return
    const item = this.find(Number(mark.dataset.annotationId))
    if (!item) return

    this.pending = null
    this.markFor = item.id
    this.showToolbar(mark.getBoundingClientRect(), item.color)
  }

  showToolbar(rect, current) {
    const toolbar = this.toolbarTarget
    const swatches = COLORS.map((color) => `
      <button type="button" class="annotation-swatch" data-color="${color}" aria-pressed="${current === color}"
              aria-label="${NAMES[color]}${current ? "" : " highlight"}"></button>`).join("")
    const label = current ? (this.find(this.markFor)?.body ? "Edit note" : "Note") : "Note"
    toolbar.innerHTML = `
      <span class="annotation-swatches">${swatches}</span>
      <span class="annotation-toolbar-sep" aria-hidden="true"></span>
      <button type="button" class="annotation-toolbar-btn" data-act="note"><i class="bi bi-pencil" aria-hidden="true"></i><span>${label}</span></button>
      ${current ? '<button type="button" class="annotation-toolbar-btn" data-act="delete" aria-label="Delete highlight"><i class="bi bi-trash3" aria-hidden="true"></i></button>' : ""}`
    toolbar.hidden = false
    document.documentElement.classList.add("annotation-selecting")

    if (this.isPhone()) {
      toolbar.style.top = toolbar.style.left = ""
      return
    }

    // Desktop: a popover above the selection (below it when there's no room
    // under the topbar), in page coordinates so it scrolls with the text.
    const width = toolbar.offsetWidth
    const height = toolbar.offsetHeight
    const topbar = 56
    const centre = rect.left + rect.width / 2
    const left = Math.max(8, Math.min(centre - width / 2, window.innerWidth - width - 8))
    const below = rect.top - topbar < height + 16
    const top = below ? rect.bottom + 12 : rect.top - height - 12
    toolbar.style.left = `${left + window.scrollX}px`
    toolbar.style.top = `${top + window.scrollY}px`
    toolbar.style.setProperty("--tip", `${Math.max(14, Math.min(centre - left, width - 14))}px`)
    toolbar.classList.toggle("is-below", below)
  }

  // Deleting takes a second click: the toolbar swaps to "Delete highlight?".
  askDeleteInToolbar(id) {
    const item = this.find(id)
    this.toolbarTarget.innerHTML = `
      <span class="annotation-toolbar-ask">${item?.body ? "Delete highlight and note?" : "Delete highlight?"}</span>
      <button type="button" class="annotation-toolbar-btn is-danger" data-act="confirm-delete">Delete</button>
      <button type="button" class="annotation-toolbar-btn" data-act="keep">Keep</button>`
    this.toolbarTarget.querySelector("[data-act='confirm-delete']").focus({ preventScroll: true })
  }

  hideToolbar() {
    if (!this.hasToolbarTarget) return
    this.toolbarTarget.hidden = true
    this.markFor = null
    document.documentElement.classList.remove("annotation-selecting")
  }

  // Keep the selection alive while a toolbar button is pressed.
  toolbarPointerDown(event) {
    if (event.target.closest("button")) event.preventDefault()
  }

  toolbarClick(event) {
    const button = event.target.closest("button")
    if (!button) return
    const color = button.dataset.color
    const act = button.dataset.act

    if (this.markFor) {
      const id = this.markFor
      if (color) this.recolor(id, color)
      else if (act === "note") { this.hideToolbar(); this.startEditing(id) }
      else if (act === "delete") this.askDeleteInToolbar(id)
      else if (act === "confirm-delete") { this.hideToolbar(); this.remove(id) }
      else if (act === "keep") this.showToolbar(this.marksFor(id)[0].getBoundingClientRect(), this.find(id)?.color)
      return
    }

    if (color) this.create(color, false)
    else if (act === "note") this.create("yellow", true)
  }

  // ---- Persistence -------------------------------------------------------------

  async create(color, withNote) {
    const pending = this.pending
    if (!pending) return
    this.pending = null
    this.hideToolbar()
    window.getSelection()?.removeAllRanges()

    const heading = sectionHeading(this.prose, pending.range.startContainer)
    const body = {
      asset_id: this.assetIdValue,
      anchor: pending.anchor,
      quote: pending.anchor.quote.exact,
      color,
      section: sectionLabel(heading) || null
    }

    const item = await this.request("POST", this.urlValue, body)
    if (!item) return this.announce("Couldn't save that highlight. Please try again.")

    this.items.push(item)
    const found = resolveAnchor(this.prose, item.anchor)
    if (found) this.paint(item, found)
    this.renderList()
    this.announce(`${NAMES[color]} highlight added.`)
    if (withNote) this.startEditing(item.id)
  }

  async recolor(id, color) {
    const item = this.find(id)
    if (!item || item.color === color) return
    item.color = color
    this.marksFor(id).forEach((mark) => { mark.dataset.color = color })
    this.toolbarTarget.querySelectorAll(".annotation-swatch").forEach((swatch) => {
      swatch.setAttribute("aria-pressed", swatch.dataset.color === color)
    })
    this.renderList()
    await this.save(item, { color })
  }

  async saveNote(id, text) {
    const item = this.find(id)
    if (!item) return
    const saved = await this.save(item, { body: text })
    if (!saved) return this.announce("Couldn't save your note. Please try again.")
    item.body = saved.body
    this.editing = null
    this.syncPin(item)
    this.renderList()
    this.announce("Note saved.")
  }

  async remove(id) {
    const item = this.find(id)
    if (!item) return
    const ok = await this.request("DELETE", `${this.urlValue}/${id}`)
    if (ok === null) return this.announce("Couldn't delete that highlight. Please try again.")
    this.unpaint(id)
    this.items = this.items.filter((other) => other.id !== id)
    if (this.editing === id) this.editing = null
    if (this.confirming === id) this.confirming = null
    this.renderList()
    this.announce("Highlight deleted.")
  }

  save(item, attrs) {
    return this.request("PATCH", `${this.urlValue}/${item.id}`, attrs)
  }

  async request(method, url, annotation) {
    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: annotation ? JSON.stringify({ annotation }) : undefined,
        credentials: "same-origin"
      })
      if (!response.ok) return null
      return response.status === 204 ? {} : await response.json()
    } catch (_) {
      return null
    }
  }

  // ---- Panel -----------------------------------------------------------------

  isOpen() {
    return this.hasPanelTarget && this.panelTarget.classList.contains("is-open")
  }

  toggle() {
    this.isOpen() ? this.close() : this.open()
  }

  open({ focusId = null } = {}) {
    if (focusId) this.focusId = focusId
    this.renderList()
    const panel = this.panelTarget
    panel.classList.add("is-open")
    panel.removeAttribute("inert")
    panel.setAttribute("aria-hidden", "false")
    this.railButtonTargets.forEach((button) => button.setAttribute("aria-expanded", "true"))
    document.documentElement.classList.add("annotations-open")
    if (this.isPhone()) document.documentElement.classList.add("annotations-sheet-open")
    window.dispatchEvent(new CustomEvent("reader-tools:opened", { detail: { tool: "annotations" } }))

    const focused = focusId && this.listTarget.querySelector(`[data-item="${focusId}"] .annotation-quote`)
    ;(focused || panel).focus({ preventScroll: true })
    focused?.scrollIntoView({ block: "nearest" })
  }

  close({ silent = false } = {}) {
    const panel = this.panelTarget
    const hadFocus = panel.contains(document.activeElement)
    panel.classList.remove("is-open")
    panel.setAttribute("aria-hidden", "true")
    panel.setAttribute("inert", "")
    this.railButtonTargets.forEach((button) => button.setAttribute("aria-expanded", "false"))
    document.documentElement.classList.remove("annotations-open", "annotations-sheet-open")
    if (hadFocus && !silent) this.railButtonTargets.find((button) => button.offsetParent)?.focus()
    if (!silent) window.dispatchEvent(new CustomEvent("reader-tools:closed", { detail: { tool: "annotations" } }))
  }

  // The AiMee button on the rail; the book-tutor controller owns the drawer.
  toggleTutor() {
    window.dispatchEvent(new CustomEvent("reader-tools:toggle", { detail: { tool: "tutor" } }))
  }

  setTutorPressed(pressed) {
    this.tutorButtonTargets.forEach((button) => button.setAttribute("aria-expanded", pressed))
  }

  // ---- List ------------------------------------------------------------------

  setFilter(event) {
    this.filter = event.currentTarget.dataset.filter
    this.renderList()
  }

  listClick(event) {
    const button = event.target.closest("[data-act]")
    if (!button) return
    const id = Number(button.dataset.id)
    switch (button.dataset.act) {
      case "go": return this.goTo(id)
      case "edit": return this.startEditing(id)
      case "cancel": this.editing = null; return this.renderList()
      case "save": return this.saveNote(id, this.listTarget.querySelector(`#annotation-note-${id}`)?.value.trim() || "")
      case "delete": return this.askDelete(id)
      case "confirm-delete": this.confirming = null; return this.remove(id)
      case "keep": this.confirming = null; this.renderList(); return this.listTarget.querySelector(`[data-item="${id}"] [data-act="delete"]`)?.focus()
    }
  }

  listKeydown(event) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && event.target.matches("textarea")) {
      event.preventDefault()
      this.saveNote(this.editing, event.target.value.trim())
    }
  }

  askDelete(id) {
    this.confirming = id
    this.editing = null
    this.renderList()
    this.listTarget.querySelector(`[data-item="${id}"] [data-act="confirm-delete"]`)?.focus()
  }

  startEditing(id) {
    this.editing = id
    this.confirming = null
    this.focusId = id
    if (!this.isOpen()) this.open({ focusId: id })
    this.renderList()
    const textarea = this.listTarget.querySelector(`#annotation-note-${id}`)
    textarea?.focus()
    textarea?.scrollIntoView({ block: "nearest" })
  }

  goTo(id) {
    const marks = this.marksFor(id)
    if (!marks.length) return
    this.focusId = id
    if (this.isPhone()) this.close({ silent: true })
    this.renderList()
    marks[0].scrollIntoView({ behavior: scrollBehavior(), block: "center" })
    marks.forEach((mark) => {
      mark.classList.remove("is-flashing")
      void mark.offsetWidth
      mark.classList.add("is-flashing")
    })
    this.announce("Jumped to your highlight.")
  }

  // ?annotation=<id> from the "My highlights" page: scroll to it and flash it.
  jumpFromUrl() {
    const id = Number(new URLSearchParams(window.location.search).get("annotation"))
    if (id && this.find(id)) this.goTo(id)
  }

  renderList() {
    if (!this.hasListTarget) return
    const live = this.items.filter((item) => item.anchor && !item.orphaned)
    const changed = this.items.filter((item) => item.anchor && item.orphaned)
    const shown = live.filter((item) => this.filter === "all" || item.color === this.filter)

    this.countTargets.forEach((badge) => {
      badge.textContent = this.items.length
      badge.hidden = this.items.length === 0
    })

    const counts = Object.fromEntries(COLORS.map((color) => [color, live.filter((item) => item.color === color).length]))
    if (this.filter !== "all" && !counts[this.filter]) this.filter = "all"
    this.filtersTarget.hidden = live.length === 0
    this.filtersTarget.innerHTML = [
      `<button type="button" class="annotation-chip" data-filter="all" aria-pressed="${this.filter === "all"}" data-action="annotations#setFilter">All</button>`,
      ...COLORS.filter((color) => counts[color]).map((color) => `
        <button type="button" class="annotation-chip" data-filter="${color}" aria-pressed="${this.filter === color}"
                data-action="annotations#setFilter" aria-label="${NAMES[color]} (${counts[color]})">
          <span class="annotation-dot" data-color="${color}" aria-hidden="true"></span>${counts[color]}
        </button>`)
    ].join("")

    if (!this.items.length) {
      this.listTarget.innerHTML = `
        <div class="annotations-empty">
          <strong>No highlights on this page yet</strong>
          Select some text and pick a colour. Add a note to explain it in your own words.
        </div>`
      return
    }

    // Group by section, in reading order.
    const ordered = [...shown].sort((a, b) => this.compareInPage(a, b))
    const groups = []
    ordered.forEach((item) => {
      const key = item.section || ""
      const last = groups[groups.length - 1]
      if (last && last.key === key) last.items.push(item)
      else groups.push({ key, items: [item] })
    })

    let html = groups.map((group) => `
      <h3 class="annotations-group">${escapeHtml(group.key || "This page")}</h3>
      ${group.items.map((item) => this.itemHtml(item, false)).join("")}`).join("")

    if (!shown.length && live.length) html += `<div class="annotations-empty">No ${NAMES[this.filter].toLowerCase()} highlights on this page.</div>`

    if (changed.length) {
      html += `
        <div class="annotations-changed">
          <p class="annotations-changed-title"><i class="bi bi-exclamation-triangle" aria-hidden="true"></i>
            ${changed.length === 1 ? "1 passage has" : `${changed.length} passages have`} changed</p>
          <p class="annotations-changed-text">This page was updated after you highlighted it, and the exact words aren't here any more. Your copy of the text and your note are kept.</p>
          ${changed.map((item) => this.itemHtml(item, true)).join("")}
        </div>`
    }

    this.listTarget.innerHTML = html
  }

  itemHtml(item, changed) {
    const editing = this.editing === item.id
    const quote = changed
      ? `<p class="annotation-quote is-changed"><span>${escapeHtml(item.quote || "")}</span></p>`
      : `<button type="button" class="annotation-quote" data-act="go" data-id="${item.id}">
           <span data-color="${item.color}">${escapeHtml(item.quote || "")}</span>
         </button>`

    const note = editing
      ? `<div class="annotation-editor">
           <label class="visually-hidden" for="annotation-note-${item.id}">Your note</label>
           <textarea id="annotation-note-${item.id}" class="form-control" rows="3" maxlength="10000"
                     placeholder="Write a note in your own words…">${escapeHtml(item.body || "")}</textarea>
           <div class="annotation-editor-actions">
             <button type="button" class="btn btn-sm btn-primary" data-act="save" data-id="${item.id}">Save note</button>
             <button type="button" class="btn btn-sm btn-outline-secondary" data-act="cancel" data-id="${item.id}">Cancel</button>
           </div>
         </div>`
      : (item.body ? `<p class="annotation-note"><i class="bi bi-pencil-fill" aria-hidden="true"></i><span>${escapeHtml(item.body)}</span></p>` : "")

    const confirming = this.confirming === item.id
    const actions = editing ? "" : confirming ? `
      <div class="annotation-confirm" role="group" aria-label="Confirm delete">
        <span>${item.body ? "Delete this highlight and its note?" : "Delete this highlight?"}</span>
        <button type="button" class="btn btn-sm btn-danger" data-act="confirm-delete" data-id="${item.id}">Delete</button>
        <button type="button" class="btn btn-sm btn-outline-secondary" data-act="keep" data-id="${item.id}">Keep</button>
      </div>` : `
      <div class="annotation-actions">
        <button type="button" class="annotation-link" data-act="edit" data-id="${item.id}">${item.body ? "Edit note" : "Add note"}</button>
        <button type="button" class="annotation-link is-danger" data-act="delete" data-id="${item.id}">Delete</button>
      </div>`

    return `<div class="annotation-item${this.focusId === item.id ? " is-focused" : ""}" data-item="${item.id}">${quote}${note}${actions}</div>`
  }

  compareInPage(a, b) {
    const ma = this.marksFor(a.id)[0]
    const mb = this.marksFor(b.id)[0]
    if (!ma || !mb) return (a.anchor?.position?.start || 0) - (b.anchor?.position?.start || 0)
    return ma.compareDocumentPosition(mb) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
  }

  // ---- Helpers ---------------------------------------------------------------

  find(id) {
    return this.items.find((item) => item.id === id)
  }

  isPhone() {
    return window.matchMedia(PHONE).matches
  }

  announce(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = ""
    requestAnimationFrame(() => { this.statusTarget.textContent = text })
  }
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
}

function truncate(value, length) {
  return value.length > length ? `${value.slice(0, length - 1)}…` : value
}
